import { ActionIcon, Button, Group, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import { PlusIcon, TrashIcon} from "@phosphor-icons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import withRouteErrorBoundary from "../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import LoaderScreen from "../../components/Loader/LoaderScreen.tsx";
import Page from "../../components/Page/Page.tsx";
import MessageContext from "../../context/MessageContext.tsx";
import api from "../../utils/api.ts";
import { AsteroidFieldSummary } from "./types.ts";
import CGT from "../../utils/CGT.ts";
import { track } from "../../utils/analytics.ts";

function AsteroidFieldsPage() {
  const navigate = useNavigate();
  const { success, error } = useContext(MessageContext);
  const [name, setName] = useState("");

  const fieldsQuery = useQuery<AsteroidFieldSummary[]>({
    queryKey: ["asteroid-fields"],
    queryFn: async () => {
      const response = await api.get("/asteroid-fields");
      return response.data.fields as AsteroidFieldSummary[];
    },
  });

  const fields = fieldsQuery.data ?? [];
  const sortedFields = useMemo(() => [...fields].sort((a, b) => b.updatedAt - a.updatedAt), [fields]);

  const createMutation = useMutation({
    mutationFn: async (fieldName: string) => {
      const response = await api.post("/asteroid-fields", { name: fieldName });
      return response.data.field as { id: string; name: string };
    },
    onSuccess: (field) => {
      track("af:field-create");
      success("Field created");
      setName("");
      fieldsQuery.refetch();
      navigate(`/asteroid-fields/${field.id}`);
    },
    onError: (e: any) => {
      error("Could not create field", e?.response?.data?.error ?? e.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      await api.delete(`/asteroid-fields/${fieldId}`);
      return fieldId;
    },
    onSuccess: () => {
      track("af:field-delete");
      success("Field deleted");
      fieldsQuery.refetch();
    },
    onError: (e: any) => {
      error("Could not delete field", e?.response?.data?.error ?? e.message);
    },
  });

  const formatUpdated = (updatedAt: number) => {
    const d = CGT.fromSeconds(updatedAt);
    return <span title={d.toFormat("yyyy-LL-dd HH:mm")}>{d.toRelative()}</span>;
  };

  const displayName = (field: AsteroidFieldSummary) => {
    if (field.fieldGalX === null || field.fieldGalY === null) {
      return field.name;
    }
    return `${field.name} (${field.fieldGalX}, ${field.fieldGalY})`;
  };

  if (fieldsQuery.isPending) {
    return <LoaderScreen message="Loading asteroid fields" />;
  }

  return (
    <Page>
      <title>Asteroid Fields</title>
      <Stack gap="md">
        <div>
          <Title order={1}>Asteroid Fields</Title>
          <Text c="dimmed">Select an existing field or create a new one.</Text>
        </div>

        <Paper withBorder p="md">
          <Group align="end" wrap="nowrap">
            <TextInput
              label="New field name"
              placeholder="Example: AF-12"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              style={{ flexGrow: 1 }}
            />
            <Button
              leftSection={<PlusIcon size={16} />}
              disabled={name.trim() === "" || createMutation.isPending}
              onClick={() => createMutation.mutate(name.trim())}
            >
              Create
            </Button>
          </Group>
        </Paper>

        <Stack gap="xs">
          {sortedFields.length === 0 ? (
            <Paper withBorder p="lg">
              <Text fw={600}>No fields yet. Create one to begin.</Text>
            </Paper>
          ) : null}

          {sortedFields.map((field) => (
            <Paper key={field.id} withBorder p="sm">
              <Group justify="space-between" align="start" wrap="nowrap">
                <div style={{ cursor: "pointer", minWidth: 0 }} onClick={() => navigate(`/asteroid-fields/${field.id}`)}>
                  <Text fw={600} truncate>
                    {displayName(field)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Updated {formatUpdated(field.updatedAt)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {field.depositCount} deposits, {field.eventCount} events
                  </Text>
                </div>
                <Group gap="xs" wrap="nowrap">
                  <Button size="xs" variant="light" onClick={() => navigate(`/asteroid-fields/${field.id}`)}>
                    Open
                  </Button>
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() => {
                      if (confirm(`Delete field \"${field.name}\"?`)) {
                        deleteMutation.mutate(field.id);
                      }
                    }}
                  >
                    <TrashIcon size={16} />
                  </ActionIcon>
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      </Stack>
    </Page>
  );
}

export default withRouteErrorBoundary(AsteroidFieldsPage);
